import clsx from 'clsx';

export interface CheckboxGroupOption {
  value: string;
  label: string;
}

interface CheckboxGroupProps {
  label?: string;
  error?: string;
  required?: boolean;
  options: CheckboxGroupOption[];
  /** Ticked option values. */
  value: string[];
  onChange: (next: string[]) => void;
  /** Options per row on >=sm screens. Defaults to 2. */
  columns?: 1 | 2;
}

/**
 * Checkbox grid for `multiSelect` product fields — several options can be ticked
 * at once, each adding its price modifier to the item.
 */
export function CheckboxGroup({
  label,
  error,
  required,
  options,
  value,
  onChange,
  columns = 2,
}: CheckboxGroupProps) {
  const toggle = (val: string) => {
    const next = value.includes(val) ? value.filter(v => v !== val) : [...value, val];
    const order = options.map(o => o.value);
    onChange(next.slice().sort((a, b) => order.indexOf(a) - order.indexOf(b)));
  };

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <span className="text-sm text-text-secondary">
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </span>
      )}
      <div className={clsx('grid gap-2', columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1')}>
        {options.map((opt) => {
          const checked = value.includes(opt.value);
          return (
            <label
              key={opt.value}
              className={clsx(
                'flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors',
                checked ? 'border-accent bg-accent/5 text-text-primary' : 'border-border-default bg-bg-field text-text-secondary',
                error && !checked ? 'border-danger' : '',
              )}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(opt.value)}
                className="h-4 w-4 shrink-0 cursor-pointer accent-accent"
              />
              <span className="min-w-0 break-words">{opt.label}</span>
            </label>
          );
        })}
      </div>
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
}
